/**
 * calculator-info-data.ts — Central technical registry for calculator information.
 *
 * All industry standards, building codes (IRC, ASTM, ANSI, TCNA), coverage factors,
 * and specifications used in the informational sections are stored here.
 * This ensures easy auditing, compliance checks, and updates.
 *
 * Last verified: July 1, 2026
 */

export interface AuditableSpecification<T> {
  value: T;
  sourceName: string;
  sourceUrl: string;
  retrievedDate: string; // YYYY-MM-DD
  standardCode?: string; // e.g. ACI 302.1R, IRC R302.6, etc.
  notes?: string;
}

export interface CalculatorSpecSheet {
  buyingGuide: {
    [key: string]: AuditableSpecification<any>;
  };
  sitePrep: {
    [key: string]: AuditableSpecification<any>;
  };
  industryStandards: {
    [key: string]: AuditableSpecification<any>;
  };
}

export const CALCULATOR_SPECS: Record<string, CalculatorSpecSheet> = {
  concrete: {
    buyingGuide: {
      standardSlabPsi: {
        value: 3000,
        sourceName: "Portland Cement Association (PCA) - Slabs on Grade",
        sourceUrl: "https://www.cement.org/cement-concrete-applications/paving/slabs-on-grade",
        retrievedDate: "2026-07-01",
        standardCode: "ACI 302.1R",
        notes: "Minimum compressive strength recommended for light residential footings and patios."
      },
      heavyDutyPsi: {
        value: 4000,
        sourceName: "American Concrete Institute (ACI) - Concrete Slab Requirements",
        sourceUrl: "https://www.concrete.org",
        retrievedDate: "2026-07-01",
        standardCode: "ACI 318",
        notes: "Recommended strength for residential driveways and vehicle slabs to prevent cracking."
      },
      premixedBagsStandard: {
        value: "ASTM C387",
        sourceName: "ASTM International - Standard Specification for Packaged, Dry, Combined Materials for Mortar and Concrete",
        sourceUrl: "https://www.astm.org/standards/c387",
        retrievedDate: "2026-07-01",
        standardCode: "ASTM C387",
        notes: "Ensures bag concrete meets minimum compressive strength when mixed per directions."
      }
    },
    sitePrep: {
      subbaseThickness: {
        value: 4, // inches
        sourceName: "National Ready Mixed Concrete Association (NRMCA)",
        sourceUrl: "https://www.nrmca.org",
        retrievedDate: "2026-07-01",
        notes: "Standard thickness for a well-draining gravel or crushed stone base under concrete slabs."
      },
      controlJointSpacing: {
        value: 10, // feet (max spacing)
        sourceName: "American Concrete Institute (ACI) - Jointing Slabs on Grade",
        sourceUrl: "https://www.concrete.org",
        retrievedDate: "2026-07-01",
        standardCode: "ACI 360R-10",
        notes: "Joint spacing should not exceed 24-30 times the slab thickness. For a 4\" slab, this is roughly 8-10 feet."
      }
    },
    industryStandards: {
      curingDays: {
        value: 7, // days
        sourceName: "American Concrete Institute (ACI) - Guide to Curing Concrete",
        sourceUrl: "https://www.concrete.org",
        retrievedDate: "2026-07-01",
        standardCode: "ACI 308R",
        notes: "Minimum curing period recommended at temperatures above 50°F to ensure concrete reaches design strength."
      }
    }
  },
  paint: {
    buyingGuide: {
      standardCoverage: {
        value: 400, // sq ft per gallon
        sourceName: "Sherwin-Williams Product Catalog & Estimator Tools",
        sourceUrl: "https://www.sherwin-williams.com",
        retrievedDate: "2026-07-01",
        notes: "Standard coverage for a single coat on smooth, primed walls."
      },
      porousCoverage: {
        value: 300, // sq ft per gallon
        sourceName: "Sherwin-Williams Product Catalog & Estimator Tools",
        sourceUrl: "https://www.sherwin-williams.com",
        retrievedDate: "2026-07-01",
        notes: "Coverage estimation for textured or porous substrates like stucco, masonry, or raw drywall."
      },
      sheenStandards: {
        value: ["Flat/Matte", "Eggshell/Satin", "Semi-Gloss", "Gloss"],
        sourceName: "Paint Quality Institute - Paint Sheens Guide",
        sourceUrl: "https://www.paintquality.com",
        retrievedDate: "2026-07-01",
        notes: "Industry classifications for reflective sheens. Semi-gloss/gloss recommended for damp areas."
      }
    },
    sitePrep: {
      tempRangeMin: {
        value: 50, // °F
        sourceName: "Sherwin-Williams - Exterior and Interior Painting Temp Guides",
        sourceUrl: "https://www.sherwin-williams.com",
        retrievedDate: "2026-07-01",
        notes: "Minimum air and substrate temperature for latex paint application."
      },
      tempRangeMax: {
        value: 90, // °F
        sourceName: "Sherwin-Williams - Exterior and Interior Painting Temp Guides",
        sourceUrl: "https://www.sherwin-williams.com",
        retrievedDate: "2026-07-01",
        notes: "Maximum recommended temperature to prevent paint from drying too quickly and cracking."
      }
    },
    industryStandards: {
      vocLimitStandard: {
        value: 50, // g/L
        sourceName: "US Environmental Protection Agency (EPA) - Architectural Coating Rules",
        sourceUrl: "https://www.epa.gov",
        retrievedDate: "2026-07-01",
        standardCode: "EPA Method 24",
        notes: "VOC limit for interior flat coatings under green building criteria."
      }
    }
  },
  flooring: {
    buyingGuide: {
      wearLayerResidentialMin: {
        value: 12, // mils
        sourceName: "Resilient Floor Covering Institute (RFCI)",
        sourceUrl: "https://rfci.com",
        retrievedDate: "2026-07-01",
        notes: "Minimum wear layer thickness recommended for residential LVP (luxury vinyl plank) under normal traffic."
      },
      wearLayerCommercialMin: {
        value: 20, // mils
        sourceName: "Resilient Floor Covering Institute (RFCI)",
        sourceUrl: "https://rfci.com",
        retrievedDate: "2026-07-01",
        notes: "Wear layer thickness recommended for high-traffic residential or commercial application."
      },
      acclimationTimeHardwood: {
        value: 72, // hours
        sourceName: "National Wood Flooring Association (NWFA) - Installation Guidelines",
        sourceUrl: "https://www.nwfa.org",
        retrievedDate: "2026-07-01",
        notes: "Standard acclimation time inside the climate-controlled space before installing solid hardwood flooring."
      }
    },
    sitePrep: {
      deflectionLimit: {
        value: "L/360",
        sourceName: "International Code Council (ICC) - International Residential Code",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-01",
        standardCode: "IRC Section R505.3.3 / Table R505.3.3(1)",
        notes: "Maximum deflection allowed for floor joists to support tile or hardwood without cracking."
      },
      concreteMoistureRHMax: {
        value: 75, // % Relative Humidity
        sourceName: "ASTM International - Standard Test Method for Determining Relative Humidity in Concrete Floor Slabs",
        sourceUrl: "https://www.astm.org/standards/f2170",
        retrievedDate: "2026-07-01",
        standardCode: "ASTM F2170",
        notes: "Standard limit for concrete slab relative humidity before installing sensitive LVP/laminate flooring."
      }
    },
    industryStandards: {
      expansionSpanMax: {
        value: 30, // feet
        sourceName: "National Wood Flooring Association (NWFA) - Expansion Guidelines",
        sourceUrl: "https://www.nwfa.org",
        retrievedDate: "2026-07-01",
        standardCode: "NWFA Section V",
        notes: "Maximum continuous span allowed before incorporating expansion joint transitions."
      }
    }
  },
  area: {
    buyingGuide: {
      standardStudSpacing: {
        value: 16, // inches
        sourceName: "International Code Council (ICC) - Wall Framing",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-01",
        standardCode: "IRC Section R602.3",
        notes: "Standard spacing for studs in load-bearing walls in residential home construction."
      },
      standardCeilingHeight: {
        value: 8, // feet
        sourceName: "International Code Council (ICC) - Minimum Room Heights",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-01",
        standardCode: "IRC Section R305.1",
        notes: "Minimum standard height for habitable space ceilings in the United States."
      }
    },
    sitePrep: {
      triangulationFormula: {
        value: "Heron's Formula",
        sourceName: "National Council of Teachers of Mathematics (NCTM)",
        sourceUrl: "https://www.nctm.org",
        retrievedDate: "2026-07-01",
        notes: "Used for calculating area of irregular rooms by breaking them down into triangles with known side lengths."
      }
    },
    industryStandards: {
      minHabitableArea: {
        value: 120, // sq ft
        sourceName: "International Code Council (ICC) - Minimum Room Dimensions",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-01",
        standardCode: "IRC Section R304.1",
        notes: "Minimum floor area required for at least one habitable room per residential dwelling."
      }
    }
  },
  fence: {
    buyingGuide: {
      cedarDurabilityClass: {
        value: "Class 2 (Moderately Durable)",
        sourceName: "US Forest Products Laboratory - Wood Handbook",
        sourceUrl: "https://www.fpl.fs.usda.gov",
        retrievedDate: "2026-07-01",
        notes: "Natural cedar heartwood possesses natural decay resistance, unlike sapwood."
      },
      treatedWoodStandard: {
        value: "AWPA U1",
        sourceName: "American Wood Protection Association (AWPA)",
        sourceUrl: "https://awpa.com",
        retrievedDate: "2026-07-01",
        standardCode: "AWPA Standard U1 UC4A",
        notes: "Requires pressure-treated wood posts touching the ground to meet ground-contact retention levels."
      }
    },
    sitePrep: {
      postDepthRule: {
        value: "1/3 of post height",
        sourceName: "American Fence Association (AFA) - Consumer Resources",
        sourceUrl: "https://www.americanfenceassociation.com",
        retrievedDate: "2026-07-01",
        notes: "Post depth should be at least 1/3 of the overall post height (e.g. 2 feet deep for a 6-foot fence)."
      },
      digSafetyLine: {
        value: 811,
        sourceName: "U.S. Common Ground Alliance (CGA)",
        sourceUrl: "https://call811.com",
        retrievedDate: "2026-07-01",
        notes: "National 'Call Before You Dig' number to request mark-out of buried utility lines before setting posts."
      }
    },
    industryStandards: {
      windLoadCapacity: {
        value: 90, // MPH
        sourceName: "ASTM International - Standard Specification for Strength Requirements of Metal Posts for Wooden Fences",
        sourceUrl: "https://www.astm.org",
        retrievedDate: "2026-07-01",
        standardCode: "ASTM F2240",
        notes: "Standard wind load structural capacity rating required for fence posts in coastal regions."
      }
    }
  },
  mulch: {
    buyingGuide: {
      optimalMulchDepthRange: {
        value: "2 to 3 inches",
        sourceName: "USDA Natural Resources Conservation Service (NRCS) - Mulching Guide",
        sourceUrl: "https://www.nrcs.usda.gov",
        retrievedDate: "2026-07-01",
        notes: "Recommended depth for weed control and moisture retention without choking plant roots."
      },
      peaGravelSizeStandard: {
        value: "3/8 inch",
        sourceName: "ASTM International - Standard Specification for Sizes of Aggregate for Road and Bridge Construction",
        sourceUrl: "https://www.astm.org/standards/d448",
        retrievedDate: "2026-07-01",
        standardCode: "ASTM D448",
        notes: "Standard aggregate size (No. 8) used for residential walkways, patios, and landscaping beds."
      }
    },
    sitePrep: {
      geotextileFabricWeight: {
        value: "3 to 4 oz/sq yd",
        sourceName: "US Department of Agriculture (USDA) Forest Service - Geotextile Spec",
        sourceUrl: "https://www.fs.usda.gov",
        retrievedDate: "2026-07-01",
        notes: "Non-woven landscape fabric weight suitable for weed suppression and gravel separating."
      }
    },
    industryStandards: {
      fireSafetyDistance: {
        value: 18, // inches
        sourceName: "National Fire Protection Association (NFPA) - Wildfire Mitigation",
        sourceUrl: "https://www.nfpa.org",
        retrievedDate: "2026-07-01",
        standardCode: "NFPA 1 Section 17.1",
        notes: "Minimum safety barrier clearance recommended for combustible wood mulch around a building foundation."
      }
    }
  },
  drywall: {
    buyingGuide: {
      residentialThicknessStandard: {
        value: 0.5, // inches
        sourceName: "Gypsum Association - Gypsum Board Installation",
        sourceUrl: "https://gypsum.org",
        retrievedDate: "2026-07-01",
        standardCode: "GA-216",
        notes: "Standard drywall panel thickness for interior residential walls with 16\" stud spacing."
      },
      fireRatedThicknessTypeX: {
        value: 0.625, // 5/8 inches
        sourceName: "International Code Council (ICC) - Garage/Dwelling Fire Separation",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-01",
        standardCode: "IRC Section R302.6",
        notes: "Requires 5/8\" Type X fire-rated drywall on garage ceilings under habitable space."
      }
    },
    sitePrep: {
      screwSpacingWalls: {
        value: 12, // inches (max spacing)
        sourceName: "Gypsum Association - Application and Finishing of Gypsum Panel Products",
        sourceUrl: "https://gypsum.org",
        retrievedDate: "2026-07-01",
        standardCode: "GA-216 / ASTM C840",
        notes: "Screws must be placed at least every 12 inches along studs on walls (ceiling spacing is typically 12\" without adhesive)."
      },
      drywallFinishLevels: {
        value: ["Level 0", "Level 1", "Level 2", "Level 3", "Level 4", "Level 5"],
        sourceName: "ASTM International - Standard Specification for Application and Finishing of Gypsum Board",
        sourceUrl: "https://www.astm.org/standards/c840",
        retrievedDate: "2026-07-01",
        standardCode: "ASTM C840",
        notes: "Level 4 is the standard residential paintable finish; Level 5 is recommended for high gloss paint or critical lighting."
      }
    },
    industryStandards: {
      fastenerStandard: {
        value: "ASTM C1002",
        sourceName: "ASTM International - Specification for Steel Self-Piercing Tapping Screws for Gypsum Board",
        sourceUrl: "https://www.astm.org",
        retrievedDate: "2026-07-01",
        standardCode: "ASTM C1002",
        notes: "Standard requirements for screws used to attach drywall board to wood or steel framing."
      }
    }
  },
  tile: {
    buyingGuide: {
      peiRatingScale: {
        value: ["Class I", "Class II", "Class III", "Class IV", "Class V"],
        sourceName: "Porcelain Enamel Institute (PEI) - Tile Selection Guide",
        sourceUrl: "https://www.tileusa.com",
        retrievedDate: "2026-07-01",
        notes: "Class III is standard for residential floors; Class IV is heavy residential/light commercial."
      },
      slipResistanceDCOF: {
        value: 0.42,
        sourceName: "ANSI Standard Specification for Tile Installation - Dynamic Coefficient of Friction",
        sourceUrl: "https://www.ansi.org",
        retrievedDate: "2026-07-01",
        standardCode: "ANSI A326.3",
        notes: "Minimum DCOF rating required for interior tiles subject to wetting (e.g. shower floors, entryways)."
      }
    },
    sitePrep: {
      substrateFlatnessTolerance: {
        value: "1/8 inch in 10 feet",
        sourceName: "Tile Council of North America (TCNA) - Substrate Flatness Guide",
        sourceUrl: "https://www.tcnatile.com",
        retrievedDate: "2026-07-01",
        standardCode: "TCNA Handbook / ANSI A108",
        notes: "Flatness tolerance required for large format tiles (tiles with any side 15 inches or longer)."
      }
    },
    industryStandards: {
      lippageToleranceMax: {
        value: "1/32 inch",
        sourceName: "ANSI Standard Specification for Installation of Ceramic Tile",
        sourceUrl: "https://www.ansi.org",
        retrievedDate: "2026-07-01",
        standardCode: "ANSI A108.19",
        notes: "Maximum height difference allowed between adjacent tiles on floors and walls."
      }
    }
  },

  // ── GROUP B — HIGH-VALUE COST ESTIMATORS ───────────────────────────────────

  solar: {
    buyingGuide: {
      panelWattageRange: {
        value: "350–500 W",
        sourceName: "Solar Energy Industries Association (SEIA) — Residential Solar Market Report Q1 2026",
        sourceUrl: "https://www.seia.org/research-resources/solar-market-insight-report-2026-q1",
        retrievedDate: "2026-07-02",
        notes: "Most residential panels sold in 2025–2026 range 380–430 W. Higher wattage panels reduce roof space needed."
      },
      avgCostPerWatt: {
        value: 2.80,
        sourceName: "SEIA / Lawrence Berkeley National Laboratory — Tracking the Sun 2025",
        sourceUrl: "https://emp.lbl.gov/tracking-sun",
        retrievedDate: "2026-07-02",
        standardCode: "LBNL Tracking the Sun 2025",
        notes: "Median installed price for residential systems under 10 kW. Includes hardware, labor, permitting, and interconnection."
      },
      systemSizeTypical: {
        value: "6–10 kW",
        sourceName: "National Renewable Energy Laboratory (NREL) — PVWatts® Calculator",
        sourceUrl: "https://pvwatts.nrel.gov",
        retrievedDate: "2026-07-02",
        notes: "Typical US residential system. Average home usage ~900 kWh/month requires ~6–8 kW to offset 80–100%."
      }
    },
    sitePrep: {
      minRoofPitch: {
        value: "10°",
        sourceName: "Solar Energy Industries Association (SEIA) — Installation Best Practices",
        sourceUrl: "https://www.seia.org/initiatives/solar-installation-best-practices",
        retrievedDate: "2026-07-02",
        notes: "Minimum roof pitch for proper self-cleaning rainwater runoff and panel efficiency."
      },
      minRoofAge: {
        value: "10 years remaining lifespan",
        sourceName: "EnergySage Solar Marketplace Research — Roof Readiness Guide",
        sourceUrl: "https://news.energysage.com/is-your-roof-ready-for-solar-panels",
        retrievedDate: "2026-07-02",
        notes: "Most installers require at least 10 years of remaining roof life before installing panels to avoid removing them for an early re-roofing."
      }
    },
    industryStandards: {
      itcRate2026: {
        value: 30,
        sourceName: "U.S. Department of Energy — Inflation Reduction Act (IRA) Solar Tax Credit",
        sourceUrl: "https://www.energy.gov/eere/solar/homeowners-guide-federal-tax-credit-solar-photovoltaics",
        retrievedDate: "2026-07-02",
        standardCode: "IRA Section 48E / 25D — 2026",
        notes: "30% Investment Tax Credit applies through 2032, then steps down. Homeowner must owe federal taxes to benefit. Consult a tax professional."
      }
    }
  },

  hvac: {
    buyingGuide: {
      seerRatingMinimum2026: {
        value: 15,
        sourceName: "U.S. Department of Energy — HVAC Efficiency Standards Update 2023",
        sourceUrl: "https://www.energy.gov/articles/doe-updates-efficiency-standards-air-conditioners-and-heat-pumps",
        retrievedDate: "2026-07-02",
        standardCode: "DOE 10 CFR Part 430 — Effective 2023",
        notes: "Minimum SEER2 rating for new residential split-system ACs in the Southeast and Southwest US as of Jan 1, 2023. Northern US minimum is SEER2 14."
      },
      systemSizePerTon: {
        value: 500,
        sourceName: "Air Conditioning Contractors of America (ACCA) — Manual J Load Calculation",
        sourceUrl: "https://www.acca.org/standards/manualj",
        retrievedDate: "2026-07-02",
        standardCode: "ACCA Manual J 8th Edition",
        notes: "Rule of thumb. Actual sizing must use a Manual J load calculation that accounts for insulation, windows, climate zone, and occupancy."
      },
      avgInstalledCostRange: {
        value: "$5,000–$12,000",
        sourceName: "HomeGuide.com — HVAC Replacement Cost Guide 2026",
        sourceUrl: "https://homeguide.com/costs/hvac-replacement-cost",
        retrievedDate: "2026-07-02",
        notes: "Typical range for central split system (2–5 tons) with standard installation. Mini-splits and high-efficiency systems cost more."
      }
    },
    sitePrep: {
      clearanceRequirements: {
        value: "18–24 inches on all sides of outdoor unit",
        sourceName: "Air Conditioning Contractors of America (ACCA) / Manufacturer Guidelines",
        sourceUrl: "https://www.acca.org",
        retrievedDate: "2026-07-02",
        notes: "Required for proper airflow and service access. Blocked airflow reduces efficiency by up to 10%."
      }
    },
    industryStandards: {
      ductLeakageTolerance: {
        value: "Total duct leakage ≤ 4 CFM25 per 100 sq ft of conditioned space",
        sourceName: "U.S. Department of Energy — ENERGY STAR New Homes Program",
        sourceUrl: "https://www.energystar.gov/newhomes",
        retrievedDate: "2026-07-02",
        standardCode: "ENERGY STAR v3.1 — Thermal Enclosure Checklist",
        notes: "Leaky ducts can waste 20–30% of conditioned air in unconditioned attics. Sealed and tested duct systems are now code in many jurisdictions."
      }
    }
  },

  roofing: {
    buyingGuide: {
      asphaltShingleLifespan: {
        value: "20–30 years (3-tab) / 30–50 years (architectural)",
        sourceName: "National Roofing Contractors Association (NRCA) — Roofing Materials Guide",
        sourceUrl: "https://www.nrca.net",
        retrievedDate: "2026-07-02",
        notes: "Architectural (dimensional) shingles dominate the market and have superior wind and impact resistance vs. 3-tab."
      },
      metalRoofLifespan: {
        value: "40–70 years",
        sourceName: "Metal Roofing Alliance (MRA) — Consumer Resource Center",
        sourceUrl: "https://www.metalroofing.com",
        retrievedDate: "2026-07-02",
        notes: "Standing seam and concealed-fastener metal roofs offer the longest residential lifespan, low maintenance, and high wind resistance."
      },
      roofingSquare: {
        value: "100 sq ft",
        sourceName: "National Roofing Contractors Association (NRCA) — Glossary of Terms",
        sourceUrl: "https://www.nrca.net",
        retrievedDate: "2026-07-02",
        notes: "All roofing materials in the US are sold by the 'square' = 100 sq ft. Order includes 10–15% overage for hips, valleys, and trim."
      }
    },
    sitePrep: {
      maxLayersAllowed: {
        value: 2,
        sourceName: "International Residential Code (IRC) — Section R905.1",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-02",
        standardCode: "IRC Section R905.1.1",
        notes: "Most US building codes allow a maximum of 2 layers of asphalt shingles. A third layer is prohibited and requires full tear-off first."
      },
      underlaymentRequirement: {
        value: "15 lb felt / ASTM D226 Type I minimum",
        sourceName: "International Residential Code (IRC) — Section R905.2.3",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-02",
        standardCode: "IRC R905.2.3 / ASTM D226",
        notes: "Self-adhering polymer-modified bitumen (ice-and-water shield) is required along eaves in cold climates (2-foot minimum from eaves into warm zone)."
      }
    },
    industryStandards: {
      windRatingStandard: {
        value: "Class H — Up to 150 MPH",
        sourceName: "UL Standard for Resistance to Wind-Driven Rain — UL 2390",
        sourceUrl: "https://www.ul.com",
        retrievedDate: "2026-07-02",
        standardCode: "UL 2390 / ASTM D3161 Class F",
        notes: "Required in hurricane-prone coastal regions. Standard ENERGY STAR shingles are Class A fire-rated and Class F wind (110 MPH) minimum."
      }
    }
  },

  kitchen: {
    buyingGuide: {
      avgKitchenSizeSqFt: {
        value: 161,
        sourceName: "National Kitchen & Bath Association (NKBA) — 2025 Design Trends Report",
        sourceUrl: "https://nkba.org/research",
        retrievedDate: "2026-07-02",
        notes: "Average US residential kitchen per NKBA 2025 survey. Premium remodels in large kitchens (200+ sq ft) can exceed $80,000."
      },
      nkbaBudgetRule: {
        value: "5–15% of home value",
        sourceName: "National Kitchen & Bath Association (NKBA) — Planning & Budgeting Guide",
        sourceUrl: "https://nkba.org/research",
        retrievedDate: "2026-07-02",
        notes: "NKBA recommends 5–15% of home value for a kitchen remodel to maintain investment value at resale. Over-investing risks over-improvement."
      },
      applianceRebatesAvailable: {
        value: "Up to $840 in IRA appliance rebates",
        sourceName: "U.S. Department of Energy — High-Efficiency Electric Home Rebate Act (HEEHRA)",
        sourceUrl: "https://www.energy.gov/policy/high-efficiency-electric-home-rebate-act",
        retrievedDate: "2026-07-02",
        standardCode: "IRA HEEHRA",
        notes: "Rebates available for ENERGY STAR electric stoves, induction ranges, and heat pump dryers. Income-based; check state availability."
      }
    },
    sitePrep: {
      permitRequirement: {
        value: "Required for structural, electrical, or plumbing changes",
        sourceName: "International Residential Code (IRC) — Chapter 1, Administration",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-02",
        standardCode: "IRC Chapter 1 / Local AHJ",
        notes: "Moving walls, adding circuits (GFCI required within 6 ft of sink per NEC 210.8), or relocating gas lines require permits in all US jurisdictions."
      }
    },
    industryStandards: {
      workTriangleStandard: {
        value: "Each leg 4–9 ft; total perimeter ≤ 26 ft",
        sourceName: "National Kitchen & Bath Association (NKBA) — Kitchen Design Guidelines",
        sourceUrl: "https://nkba.org/design-guidelines/kitchen",
        retrievedDate: "2026-07-02",
        standardCode: "NKBA Kitchen Planning Guideline 2",
        notes: "Classic work triangle (sink–range–refrigerator). NKBA 2024 update recognizes work zones as a flexible alternative for larger kitchens."
      }
    }
  },

  bathroom: {
    buyingGuide: {
      avgBathRemodel2026: {
        value: "$35,000",
        sourceName: "National Kitchen & Bath Association (NKBA) — 2026 Cost & Trends Report",
        sourceUrl: "https://nkba.org/research",
        retrievedDate: "2026-07-02",
        notes: "National median cost for a full master bathroom renovation. Simple half-bath refreshes average $3,000–$8,000."
      },
      narJoyScore: {
        value: 9.8,
        sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
        sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
        retrievedDate: "2026-07-02",
        notes: "Score of 9.8 out of 10 for homeowner satisfaction after bathroom renovation. Highest ranked interior renovation in NAR 2025 survey."
      },
      waterSenseFixtures: {
        value: "Saves 700+ gallons/year per fixture",
        sourceName: "U.S. Environmental Protection Agency (EPA) — WaterSense Program",
        sourceUrl: "https://www.epa.gov/watersense",
        retrievedDate: "2026-07-02",
        standardCode: "EPA WaterSense",
        notes: "Certified toilets use ≤1.28 GPF vs standard 1.6 GPF; faucets ≤1.5 GPM vs 2.2 GPM. Many utilities offer rebates for WaterSense fixtures."
      }
    },
    sitePrep: {
      ventilationRequired: {
        value: "50 CFM minimum (bathroom under 100 sq ft)",
        sourceName: "American Society of Heating, Refrigerating and Air-Conditioning Engineers (ASHRAE)",
        sourceUrl: "https://www.ashrae.org",
        retrievedDate: "2026-07-02",
        standardCode: "ASHRAE 62.2 / IRC Section R303.3",
        notes: "Exhaust fan must duct to exterior. Circulating fans that recirculate into the attic are code violations. ENERGY STAR fans are 60% more efficient."
      },
      gfciRequired: {
        value: "All outlets within 6 ft of any water source",
        sourceName: "National Fire Protection Association (NFPA) — National Electrical Code (NEC)",
        sourceUrl: "https://www.nfpa.org/NEC",
        retrievedDate: "2026-07-02",
        standardCode: "NEC 210.8(A)(1)",
        notes: "Ground-Fault Circuit Interrupter (GFCI) protection is required for all bathroom receptacles regardless of distance from water. Updated in NEC 2023."
      }
    },
    industryStandards: {
      minimumClearances: {
        value: "15 in from center of toilet to each side wall; 21 in in front of toilet",
        sourceName: "International Residential Code (IRC) — Bathroom Plumbing",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-02",
        standardCode: "IRC Section P2705.1",
        notes: "Minimum clearances under IRC. ADA-accessible layouts require 18 in from center to side wall and 60 in turning radius for wheelchair access."
      }
    }
  },

  windows: {
    buyingGuide: {
      energyStarZones: {
        value: "4 climate zones (Northern, North/Central, South/Central, Southern)",
        sourceName: "ENERGY STAR — Windows, Doors & Skylights Product Finder",
        sourceUrl: "https://www.energystar.gov/products/building_products/residential_windows_doors_and_skylights",
        retrievedDate: "2026-07-02",
        standardCode: "ENERGY STAR Windows Version 7.0",
        notes: "Each zone has different U-factor and SHGC requirements. Northern zones need low U-factor (≤0.22); Southern zones prioritize low SHGC (≤0.25) to block solar heat gain."
      },
      uFactorExplained: {
        value: "≤0.22 (Northern US) / ≤0.40 (Southern US)",
        sourceName: "National Fenestration Rating Council (NFRC) — Ratings Explained",
        sourceUrl: "https://www.nfrc.org/ratings",
        retrievedDate: "2026-07-02",
        standardCode: "NFRC 100 — U-factor",
        notes: "U-factor measures heat loss rate (lower = better insulation). Required by NFRC label on all rated windows. Triple-pane can reach U-0.12."
      },
      narRoiVinyl: {
        value: 74,
        sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
        sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
        retrievedDate: "2026-07-02",
        notes: "Vinyl window replacement recovers 74% of cost at resale. Wood window replacement (71%). Both rank highly on the NAR Joy Score (8.5/10)."
      }
    },
    sitePrep: {
      flashing: {
        value: "Self-adhering flashing membrane on all 4 sides of rough opening",
        sourceName: "ASTM International — Standard Practice for Application of Exterior Windows, Doors, and Skylights",
        sourceUrl: "https://www.astm.org/standards/e2112",
        retrievedDate: "2026-07-02",
        standardCode: "ASTM E2112",
        notes: "Improper flashing is the #1 cause of window-related water intrusion damage. Pan flashing on the sill is required before insert installation."
      }
    },
    industryStandards: {
      impactRatingRequirement: {
        value: "ASTM E1886 / AAMA 506 impact-rated for hurricane zones",
        sourceName: "Florida Building Code (FBC) — High-Velocity Hurricane Zone (HVHZ) Standards",
        sourceUrl: "https://floridabuilding.org",
        retrievedDate: "2026-07-02",
        standardCode: "FBC / ASTM E1886 + ASTM E1996",
        notes: "Impact-rated windows are required in HVHZ (Miami-Dade, Broward) and wind-borne debris regions in Florida and coastal areas of other states."
      }
    }
  },

  "water-heater": {
    buyingGuide: {
      waterHeatingEnergyUse: {
        value: "about 18% of home energy use",
        sourceName: "U.S. Department of Energy — Energy Saver Water Heating",
        sourceUrl: "https://www.energy.gov/energysaver/water-heating",
        retrievedDate: "2026-07-07",
        notes: "DOE states water heating is typically the second-largest energy expense in a home."
      },
      hpwhTaxCredit: {
        value: "30% up to $2,000",
        sourceName: "Internal Revenue Service — Energy Efficient Home Improvement Credit",
        sourceUrl: "https://www.irs.gov/credits-deductions/energy-efficient-home-improvement-credit",
        retrievedDate: "2026-07-07",
        standardCode: "IRC Section 25C",
        notes: "Applies to qualifying electric or natural gas heat pump water heaters placed in service before the statutory cutoff."
      }
    },
    sitePrep: {
      existingHomeRule: {
        value: "Primary residence, existing home",
        sourceName: "Internal Revenue Service — Energy Efficient Home Improvement Credit",
        sourceUrl: "https://www.irs.gov/credits-deductions/energy-efficient-home-improvement-credit",
        retrievedDate: "2026-07-07",
        notes: "The IRS limits this credit to a main home located in the United States, generally an existing home."
      }
    },
    industryStandards: {
      sizingStandard: {
        value: "First-hour rating / gallons per minute",
        sourceName: "U.S. Department of Energy — Sizing a New Water Heater",
        sourceUrl: "https://www.energy.gov/energysaver/sizing-new-water-heater",
        retrievedDate: "2026-07-07",
        notes: "Storage water heaters are sized by first-hour rating; tankless heaters are sized by flow rate and temperature rise."
      }
    }
  },

  "roof-shingles": {
    buyingGuide: {
      roofingSquare: {
        value: "100 sq ft",
        sourceName: "National Roofing Contractors Association (NRCA) — Roofing Glossary",
        sourceUrl: "https://www.nrca.net",
        retrievedDate: "2026-07-07",
        notes: "US roofing materials are ordered by the square."
      },
      bundlesPerSquare: {
        value: "3 bundles per square typical",
        sourceName: "Asphalt Roofing Manufacturers Association (ARMA)",
        sourceUrl: "https://www.asphaltroofing.org",
        retrievedDate: "2026-07-07",
        notes: "Many asphalt shingle products cover one square with three bundles; some premium products require four."
      }
    },
    sitePrep: {
      underlayment: {
        value: "Underlayment required below asphalt shingles",
        sourceName: "International Residential Code — Roof Assemblies",
        sourceUrl: "https://codes.iccsafe.org/content/IRC2024P1/chapter-9-roof-assemblies",
        retrievedDate: "2026-07-07",
        standardCode: "IRC R905.2.3",
        notes: "Local amendments may require ice barrier at eaves in cold climates."
      }
    },
    industryStandards: {
      windClassification: {
        value: "ASTM D3161 / ASTM D7158",
        sourceName: "ASTM International — Asphalt Shingle Wind Resistance Standards",
        sourceUrl: "https://www.astm.org",
        retrievedDate: "2026-07-07",
        standardCode: "ASTM D3161 / ASTM D7158",
        notes: "Wind classification affects fastening schedule and product selection in coastal regions."
      }
    }
  },

  siding: {
    buyingGuide: {
      drainagePlane: {
        value: "Continuous sealed drainage plane",
        sourceName: "DOE Building America Solution Center — Drainage Plane Behind Exterior Wall Cladding",
        sourceUrl: "https://basc.pnnl.gov/resource-guides/drainage-plane-behind-exterior-wall-cladding",
        retrievedDate: "2026-07-07",
        notes: "House wrap or another WRB should be sealed and lapped over flashing to direct bulk water out."
      },
      vinylInstallationManual: {
        value: "IRC/IBC-based installation instructions",
        sourceName: "Polymeric Exterior Products Association — Vinyl Siding Installation Manual",
        sourceUrl: "https://polymericexteriors.org/installation/installation-manual/",
        retrievedDate: "2026-07-07",
        notes: "PEPA notes its manual is based partly on ICC residential and building code sections for vinyl siding and soffit."
      }
    },
    sitePrep: {
      wallPenetrations: {
        value: "Seal penetrations and lap flashings shingle-style",
        sourceName: "DOE Building America Solution Center",
        sourceUrl: "https://basc.pnnl.gov/resource-guides/drainage-plane-behind-exterior-wall-cladding",
        retrievedDate: "2026-07-07",
        notes: "Common penetrations include hose bibbs, vents, line sets, wiring, windows, and doors."
      }
    },
    industryStandards: {
      vinylStandard: {
        value: "ASTM D3679",
        sourceName: "ASTM International — Rigid PVC Siding Standard",
        sourceUrl: "https://www.astm.org",
        retrievedDate: "2026-07-07",
        standardCode: "ASTM D3679",
        notes: "Standard specification referenced for rigid vinyl siding products."
      }
    }
  },

  driveway: {
    buyingGuide: {
      concreteDrivewayThickness: {
        value: "4 inches typical residential slab",
        sourceName: "Portland Cement Association — Concrete Driveways",
        sourceUrl: "https://www.cement.org",
        retrievedDate: "2026-07-07",
        notes: "Heavier vehicles or poor subgrade can require thicker concrete and more base prep."
      },
      gravelRoadCrown: {
        value: "Crown and drainage are critical",
        sourceName: "Federal Highway Administration — Gravel Roads Construction & Maintenance Guide",
        sourceUrl: "https://www.fhwa.dot.gov/construction/pubs/ots15002.pdf",
        retrievedDate: "2026-07-07",
        notes: "Driveways use the same drainage principle: shed water off the surface before it creates ruts or potholes."
      }
    },
    sitePrep: {
      callBeforeDig: {
        value: 811,
        sourceName: "U.S. Common Ground Alliance — Call 811",
        sourceUrl: "https://call811.com",
        retrievedDate: "2026-07-07",
        notes: "Excavation for driveway replacement can hit irrigation, gas, electrical, or telecom lines."
      }
    },
    industryStandards: {
      concreteCure: {
        value: "28 days to design strength",
        sourceName: "Portland Cement Association — Concrete Curing",
        sourceUrl: "https://www.cement.org",
        retrievedDate: "2026-07-07",
        standardCode: "ACI 308R",
        notes: "Concrete can often accept foot traffic earlier, but full design strength is conventionally based on 28-day curing."
      }
    }
  },

  "home-addition": {
    buyingGuide: {
      remodelingTrend: {
        value: "Renovate instead of moving",
        sourceName: "Harvard Joint Center for Housing Studies — Improving America's Housing",
        sourceUrl: "https://www.jchs.harvard.edu/research-areas/remodeling",
        retrievedDate: "2026-07-07",
        notes: "Remodeling demand is tied to aging housing stock, locked-in mortgage rates, and homeowners adapting existing homes."
      },
      taxCreditExistingHome: {
        value: "Existing home you improve or add onto",
        sourceName: "Internal Revenue Service — Energy Efficient Home Improvement Credit",
        sourceUrl: "https://www.irs.gov/credits-deductions/energy-efficient-home-improvement-credit",
        retrievedDate: "2026-07-07",
        standardCode: "IRC Section 25C",
        notes: "IRS language explicitly includes an existing home you improve or add onto for qualifying efficiency improvements."
      }
    },
    sitePrep: {
      habitableRoomHeight: {
        value: "7 ft minimum ceiling height typical",
        sourceName: "International Residential Code — Room Areas and Ceiling Heights",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-07",
        standardCode: "IRC R305",
        notes: "Habitable rooms must also meet local light, ventilation, egress, and smoke/CO alarm rules."
      }
    },
    industryStandards: {
      emergencyEscapeOpening: {
        value: "Emergency escape and rescue opening for sleeping rooms",
        sourceName: "International Residential Code — Emergency Escape and Rescue Openings",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-07",
        standardCode: "IRC R310",
        notes: "Bedroom additions require compliant emergency escape and rescue openings unless local alternatives apply."
      }
    }
  },

  deck: {
    buyingGuide: {
      compositeDeckBrands: {
        value: "Trex, TimberTech, Fiberon (most popular composite brands)",
        sourceName: "North American Deck and Railing Association (NADRA) — Market Report 2025",
        sourceUrl: "https://www.nadra.org",
        retrievedDate: "2026-07-02",
        notes: "Composite decking makes up 45%+ of new deck installations. No painting, staining, or sealing required. Warranty typically 25–30 years."
      },
      lumberPricesVolatile: {
        value: "Lumber prices fluctuate 20–60% seasonally",
        sourceName: "Random Lengths — Framing Lumber Composite Price Index",
        sourceUrl: "https://www.randomlengths.com",
        retrievedDate: "2026-07-02",
        notes: "Always verify current lumber prices with local suppliers. This calculator uses 2026 national averages from Angi/HomeGuide surveys."
      },
      avgDeckRoi: {
        value: 50,
        sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
        sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
        retrievedDate: "2026-07-02",
        notes: "New deck recovers ~50% at resale. Joy Score of 9.3/10. Composite decks may recover more due to longevity and lower ongoing maintenance costs."
      }
    },
    sitePrep: {
      permitRequired: {
        value: "Required for any deck attached to a house or over 30 inches high",
        sourceName: "International Residential Code (IRC) — Decks and Exterior Balconies",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-02",
        standardCode: "IRC Section R507",
        notes: "IRC 2021 R507 provides prescriptive deck construction details. Footing depth must reach below frost line. Ledger attachment requires approved fasteners."
      },
      footingDepth: {
        value: "Below local frost depth (12–48 inches depending on zone)",
        sourceName: "International Residential Code (IRC) — Foundation Walls and Footings",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-02",
        standardCode: "IRC Section R403.1.4",
        notes: "USDA frost depth maps show frost line depths by county. Footings above frost depth will heave and shift the deck in winter climates."
      }
    },
    industryStandards: {
      railingHeightRequirement: {
        value: "36 in (decks <30 in high) / 42 in (decks ≥30 in)",
        sourceName: "International Residential Code (IRC) — Guards",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-02",
        standardCode: "IRC Section R312",
        notes: "Balusters must be spaced so a 4-inch sphere cannot pass through. Horizontal rails that act as ladder rungs are prohibited under IRC 2021."
      }
    }
  },

  roi: {
    buyingGuide: {
      narReport2025: {
        value: "NAR/NARI Remodeling Impact Report 2025",
        sourceName: "National Association of Realtors (NAR) and National Association of the Remodeling Industry (NARI)",
        sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
        retrievedDate: "2026-07-02",
        standardCode: "NAR 2025 Remodeling Impact Report",
        notes: "Annual joint report covering cost recovery percentages, Joy Score (homeowner satisfaction), and top projects ranked by ROI and livability."
      },
      joyScoreExplained: {
        value: "Scale of 1–10 (10 = greatest happiness)",
        sourceName: "National Association of Realtors (NAR) — Remodeling Impact Methodology",
        sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
        retrievedDate: "2026-07-02",
        notes: "The Joy Score represents how happy homeowners are with the project in their daily life, separate from the financial ROI question."
      },
      overImprovementRisk: {
        value: "Keep any single project ≤ 15% of home value",
        sourceName: "National Association of Realtors (NAR) — Seller Guide",
        sourceUrl: "https://www.nar.realtor",
        retrievedDate: "2026-07-02",
        notes: "Over-improving beyond neighborhood comparables results in an appraisal ceiling that prevents full cost recovery. This is called 'over-improvement'."
      }
    },
    sitePrep: {
      appraiserNote: {
        value: "Appraisers use comparable sales (comps) — not renovation costs — to set value",
        sourceName: "Appraisal Institute — The Appraisal of Real Estate, 15th Edition",
        sourceUrl: "https://www.appraisalinstitute.org",
        retrievedDate: "2026-07-02",
        notes: "Even $100,000 spent on renovations may not add $100,000 to appraised value. Neighborhood price ceiling limits ROI in lower-value markets."
      }
    },
    industryStandards: {
      realtorNarJoyTop: {
        value: "Primary suite addition — Joy Score 10/10",
        sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
        sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
        retrievedDate: "2026-07-02",
        notes: "Projects with highest Joy Scores: primary suite addition (10), complete kitchen (10), roof replacement (10), attic conversion (9.8), closet renovation (9.5)."
      }
    }
  },

  pool: {
    buyingGuide: {
      pumpEfficiency: {
        value: "Variable-speed pool pumps can reduce pool pump energy use substantially versus single-speed pumps",
        sourceName: "U.S. Department of Energy — Swimming Pool Heating and Pumps",
        sourceUrl: "https://www.energy.gov/energysaver/swimming-pool-heating",
        retrievedDate: "2026-07-07",
        notes: "Operating cost depends on pump schedule, electricity rate, turnover target, and local pool season."
      },
      ownershipCost: {
        value: "Pool estimates should include shell, excavation, equipment, electrical, decking, fencing, and yearly maintenance",
        sourceName: "U.S. Consumer Product Safety Commission — Pool Safely",
        sourceUrl: "https://www.poolsafely.gov",
        retrievedDate: "2026-07-07",
        notes: "Safety barriers and self-closing gates are not optional in many jurisdictions."
      }
    },
    sitePrep: {
      barrierRequirement: {
        value: "Use a 4-sided barrier with self-closing, self-latching gates where required",
        sourceName: "U.S. Consumer Product Safety Commission — Safety Barrier Guidelines for Residential Pools",
        sourceUrl: "https://www.cpsc.gov/s3fs-public/362%20Safety%20Barrier%20Guidelines%20for%20Residential%20Pools.pdf",
        retrievedDate: "2026-07-07",
        standardCode: "CPSC Publication 362",
        notes: "Local code may set stricter height, latch, alarm, and access-control requirements."
      }
    },
    industryStandards: {
      electricalBonding: {
        value: "Pool electrical work should be permitted and inspected",
        sourceName: "National Fire Protection Association — National Electrical Code",
        sourceUrl: "https://www.nfpa.org/codes-and-standards/nfpa-70-standard-development/70",
        retrievedDate: "2026-07-07",
        standardCode: "NEC Article 680",
        notes: "Pool pumps, bonding, grounding, lighting, and receptacle spacing are specialist electrical work."
      }
    }
  },

  countertop: {
    buyingGuide: {
      kitchenBudgetShare: {
        value: "Countertops commonly sit inside the kitchen finish-material budget, not the structural scope",
        sourceName: "National Kitchen & Bath Association — Kitchen Planning Guidelines",
        sourceUrl: "https://nkba.org",
        retrievedDate: "2026-07-07",
        notes: "Material, slab availability, edge work, seams, sink cutouts, and demolition drive quote differences."
      },
      foodContactSurface: {
        value: "Kitchen work surfaces should be cleanable, durable, and compatible with food-prep use",
        sourceName: "U.S. Food and Drug Administration — Food Code",
        sourceUrl: "https://www.fda.gov/food/fda-food-code/food-code-2022",
        retrievedDate: "2026-07-07",
        standardCode: "FDA Food Code 2022",
        notes: "Residential kitchens are not regulated like commercial kitchens, but cleanability is a practical selection criterion."
      }
    },
    sitePrep: {
      templateSequence: {
        value: "Cabinets must be set, level, and secured before countertop templating",
        sourceName: "National Kitchen & Bath Association — Kitchen Installation Practices",
        sourceUrl: "https://nkba.org",
        retrievedDate: "2026-07-07",
        notes: "Sink, faucet, cooktop, and appliance specs should be available before fabrication."
      }
    },
    industryStandards: {
      silicaSafety: {
        value: "Stone fabrication can expose workers to respirable crystalline silica",
        sourceName: "Occupational Safety and Health Administration — Respirable Crystalline Silica",
        sourceUrl: "https://www.osha.gov/silica-crystalline",
        retrievedDate: "2026-07-07",
        standardCode: "29 CFR 1926.1153",
        notes: "Homeowners should use fabricators following wet cutting, dust control, and respiratory protection rules."
      }
    }
  },

  gutters: {
    buyingGuide: {
      drainagePurpose: {
        value: "Gutters and downspouts should discharge stormwater away from the foundation",
        sourceName: "U.S. Environmental Protection Agency — Soak Up the Rain",
        sourceUrl: "https://www.epa.gov/soakuptherain/soak-rain-downspout-disconnection",
        retrievedDate: "2026-07-07",
        notes: "Extensions, splash blocks, rain gardens, and underground drain lines reduce foundation wetting."
      }
    },
    sitePrep: {
      downspoutSpacing: {
        value: "Long roof runs may need additional downspouts to avoid overflow",
        sourceName: "Sheet Metal and Air Conditioning Contractors' National Association — Architectural Sheet Metal Manual",
        sourceUrl: "https://www.smacna.org",
        retrievedDate: "2026-07-07",
        notes: "Sizing depends on roof drainage area, rainfall intensity, gutter profile, slope, and outlet size."
      }
    },
    industryStandards: {
      waterManagement: {
        value: "Control roof runoff before it reaches cladding or foundation walls",
        sourceName: "DOE Building America Solution Center — Roof/Wall Water Management",
        sourceUrl: "https://basc.pnnl.gov",
        retrievedDate: "2026-07-07",
        notes: "Gutters are part of the same moisture-management system as flashing, siding, grade, and foundation drainage."
      }
    }
  },

  "basement-finishing": {
    buyingGuide: {
      roi: {
        value: "Basement conversion to living area: 71% cost recovery in NAR 2025 data",
        sourceName: "National Association of Realtors / NARI — 2025 Remodeling Impact Report",
        sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
        retrievedDate: "2026-07-07",
        notes: "ROI varies with ceiling height, legal bedrooms, moisture history, and local comparable sales."
      }
    },
    sitePrep: {
      moistureFirst: {
        value: "Fix water intrusion and moisture sources before enclosing basement walls",
        sourceName: "U.S. Environmental Protection Agency — Mold Resources",
        sourceUrl: "https://www.epa.gov/mold",
        retrievedDate: "2026-07-07",
        notes: "Wet materials hidden behind finished walls can create mold and durability problems."
      },
      habitableHeight: {
        value: "Basement habitable rooms typically require minimum ceiling height and compliant ventilation/light",
        sourceName: "International Residential Code — Ceiling Height",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-07",
        standardCode: "IRC R305",
        notes: "Local amendments and existing-building rules can alter requirements."
      }
    },
    industryStandards: {
      egressBedrooms: {
        value: "Basement sleeping rooms require emergency escape and rescue openings",
        sourceName: "International Residential Code — Emergency Escape and Rescue Openings",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-07",
        standardCode: "IRC R310",
        notes: "Window well, sill height, net clear opening, and ladder rules matter for legal bedrooms."
      }
    }
  },

  insulation: {
    buyingGuide: {
      doeRValues: {
        value: "DOE/ENERGY STAR attic targets commonly range from R-30 to R-60 by climate zone",
        sourceName: "ENERGY STAR — Recommended Home Insulation R-Values",
        sourceUrl: "https://www.energystar.gov/saveathome/seal_insulate/identify-problems-you-want-fix/diy-checks-inspections/insulation-r-values",
        retrievedDate: "2026-07-07",
        notes: "Targets are total R-value after accounting for existing insulation."
      },
      taxCreditStatus: {
        value: "Section 25C Energy Efficient Home Improvement Credit was terminated for property placed in service after 2025 by Public Law 119-21",
        sourceName: "U.S. Congress / GovInfo — Public Law 119-21",
        sourceUrl: "https://www.govinfo.gov/app/details/PLAW-119publ21",
        retrievedDate: "2026-07-07",
        standardCode: "IRC Section 25C",
        notes: "This calculator assumes no 25C insulation credit for 2026 projects."
      }
    },
    sitePrep: {
      airSealing: {
        value: "Air sealing should be completed before adding attic insulation",
        sourceName: "U.S. Department of Energy — Air Sealing Your Home",
        sourceUrl: "https://www.energy.gov/energysaver/air-sealing-your-home",
        retrievedDate: "2026-07-07",
        notes: "Open bypasses can undermine added R-value and move humid air into the attic."
      }
    },
    industryStandards: {
      rValueDefinition: {
        value: "R-value measures resistance to conductive heat flow; higher R-value means greater insulating effectiveness",
        sourceName: "U.S. Department of Energy — Insulation",
        sourceUrl: "https://www.energy.gov/energysaver/insulation",
        retrievedDate: "2026-07-07",
        notes: "Performance also depends on installation quality, compression, moisture, and air leakage."
      }
    }
  },

  "cabinet-refacing": {
    buyingGuide: {
      refacingScope: {
        value: "Refacing keeps cabinet boxes in place and replaces doors, drawer fronts, visible skins, and hardware",
        sourceName: "National Kitchen & Bath Association — Kitchen Planning Guidelines",
        sourceUrl: "https://nkba.org",
        retrievedDate: "2026-07-07",
        notes: "Refacing is a cosmetic and functional refresh only when cabinet boxes are structurally sound."
      },
      contractorScreening: {
        value: "Compare written scopes, licenses, warranties, and payment terms before hiring",
        sourceName: "Federal Trade Commission — Hiring a Contractor",
        sourceUrl: "https://consumer.ftc.gov/articles/hiring-contractor",
        retrievedDate: "2026-07-07",
        notes: "Cabinet projects often overlap with countertop, plumbing, and electrical work."
      }
    },
    sitePrep: {
      countertopSequence: {
        value: "Countertops should be protected or removed before full cabinet replacement",
        sourceName: "National Kitchen & Bath Association — Kitchen Installation Practices",
        sourceUrl: "https://nkba.org",
        retrievedDate: "2026-07-07",
        notes: "Changing cabinet boxes can break existing stone or laminate countertops."
      }
    },
    industryStandards: {
      kitchenGfci: {
        value: "Kitchen receptacles serving countertop surfaces require GFCI protection",
        sourceName: "National Fire Protection Association — National Electrical Code",
        sourceUrl: "https://www.nfpa.org/codes-and-standards/nfpa-70-standard-development/70",
        retrievedDate: "2026-07-07",
        standardCode: "NEC 210.8",
        notes: "Electrical scope can enter a cabinet project when layouts, islands, or appliances move."
      }
    }
  },

  "backup-generator": {
    buyingGuide: {
      essentialLoads: {
        value: "Size generators from running watts plus starting surge for motor loads",
        sourceName: "Ready.gov — Power Outages",
        sourceUrl: "https://www.ready.gov/power-outages",
        retrievedDate: "2026-07-07",
        notes: "Refrigerators, pumps, blowers, and air conditioners need starting allowance."
      },
      coSafety: {
        value: "Never run portable generators inside homes, garages, basements, crawlspaces, or near openings",
        sourceName: "U.S. Consumer Product Safety Commission — Generators and Engine-Driven Tools",
        sourceUrl: "https://www.cpsc.gov/Safety-Education/Safety-Education-Centers/Carbon-Monoxide-Information-Center/Generators-and-Engine-Driven-Tools",
        retrievedDate: "2026-07-07",
        notes: "Carbon monoxide poisoning is the major life-safety risk with portable generators."
      }
    },
    sitePrep: {
      stormUse: {
        value: "Use generators outdoors and protect cords from standing water during outages",
        sourceName: "Federal Emergency Management Agency — Use Generators Safely at Home",
        sourceUrl: "https://www.fema.gov/fact-sheet/use-generators-safely-home",
        retrievedDate: "2026-07-07",
        notes: "Wet weather and hurricane outages increase electrical and CO risks."
      }
    },
    industryStandards: {
      transferEquipment: {
        value: "Generators connected to premises wiring need listed transfer equipment to prevent backfeed",
        sourceName: "National Fire Protection Association — National Electrical Code",
        sourceUrl: "https://www.nfpa.org/codes-and-standards/nfpa-70-standard-development/70",
        retrievedDate: "2026-07-07",
        standardCode: "NEC Article 702",
        notes: "Manual or automatic transfer switches isolate utility power from generator power."
      }
    }
  },

  "ev-charger": {
    buyingGuide: {
      level2Power: {
        value: "Residential Level 2 charging commonly uses 240 V service and can range from 2.9 to 19.2 kW",
        sourceName: "U.S. Department of Energy AFDC — Electric Vehicle Charging Stations",
        sourceUrl: "https://afdc.energy.gov/fuels/electricity-stations",
        retrievedDate: "2026-07-07",
        notes: "AFDC lists about 25 miles of range per hour for Level 2 charging under typical assumptions."
      },
      taxCreditStatus: {
        value: "Section 30C alternative fuel refueling property credit expired after June 30, 2026 for this 2026 calculator assumption",
        sourceName: "Internal Revenue Service — About Form 8911",
        sourceUrl: "https://www.irs.gov/forms-pubs/about-form-8911",
        retrievedDate: "2026-07-07",
        standardCode: "IRC Section 30C",
        notes: "State, local, and utility charger rebates may still be available."
      }
    },
    sitePrep: {
      panelCapacity: {
        value: "Electrical panel capacity and circuit distance are the main cost drivers",
        sourceName: "U.S. Department of Energy AFDC — Charging Infrastructure Procurement and Installation",
        sourceUrl: "https://afdc.energy.gov/fuels/electricity-infrastructure",
        retrievedDate: "2026-07-07",
        notes: "Detached garages, trenching, and service upgrades materially change cost."
      }
    },
    industryStandards: {
      evseCircuit: {
        value: "EV charging equipment is treated as a continuous electrical load",
        sourceName: "National Fire Protection Association — National Electrical Code",
        sourceUrl: "https://www.nfpa.org/codes-and-standards/nfpa-70-standard-development/70",
        retrievedDate: "2026-07-07",
        standardCode: "NEC Article 625",
        notes: "Licensed electricians should verify conductor size, breaker size, GFCI rules, and load calculations."
      }
    }
  },

  "siding-material": {
    buyingGuide: {
      drainagePlane: {
        value: "Install a continuous drainage plane behind exterior wall cladding",
        sourceName: "DOE Building America Solution Center — Drainage Plane Behind Exterior Wall Cladding",
        sourceUrl: "https://basc.pnnl.gov/resource-guides/drainage-plane-behind-exterior-wall-cladding",
        retrievedDate: "2026-07-07",
        notes: "House wrap and flashing details protect sheathing from wind-driven rain."
      },
      wasteFactor: {
        value: "Use 10% to 20% waste depending on wall complexity, gables, and trim cuts",
        sourceName: "Polymeric Exterior Products Association — Vinyl Siding Installation Manual",
        sourceUrl: "https://polymericexteriors.org/installation/installation-manual/",
        retrievedDate: "2026-07-07",
        notes: "Complex elevations, short returns, dormers, and diagonal cuts increase waste."
      }
    },
    sitePrep: {
      flashingOpenings: {
        value: "Wall penetrations and openings should be flashed and lapped shingle-style",
        sourceName: "DOE Building America Solution Center",
        sourceUrl: "https://basc.pnnl.gov",
        retrievedDate: "2026-07-07",
        notes: "Windows and doors are the highest-risk siding interfaces."
      }
    },
    industryStandards: {
      vinylStandard: {
        value: "Vinyl siding installation should follow manufacturer instructions and applicable code",
        sourceName: "Polymeric Exterior Products Association — Vinyl Siding Installation Manual",
        sourceUrl: "https://polymericexteriors.org/installation/installation-manual/",
        retrievedDate: "2026-07-07",
        standardCode: "ASTM D3679 / IRC R703",
        notes: "Fastener spacing, expansion clearance, and locking details affect performance."
      }
    }
  },

  "retaining-wall": {
    buyingGuide: {
      drainage: {
        value: "Drainage and free-draining backfill are critical behind retaining walls",
        sourceName: "National Concrete Masonry Association — Segmental Retaining Wall Best Practices",
        sourceUrl: "https://ncma.org",
        retrievedDate: "2026-07-07",
        notes: "Water pressure behind the wall is a common failure driver."
      }
    },
    sitePrep: {
      call811: {
        value: "Call 811 before excavating for wall base, drain pipe, or geogrid",
        sourceName: "U.S. Common Ground Alliance — Call 811",
        sourceUrl: "https://call811.com",
        retrievedDate: "2026-07-07",
        notes: "Retaining wall excavation can cross gas, power, irrigation, telecom, and drainage lines."
      }
    },
    industryStandards: {
      permitThreshold: {
        value: "Walls around 4 ft high or supporting surcharge commonly trigger permit or engineering review",
        sourceName: "International Residential Code / local building departments",
        sourceUrl: "https://codes.iccsafe.org",
        retrievedDate: "2026-07-07",
        standardCode: "IRC R404 / local amendments",
        notes: "Measured height, slope, fence load, driveway load, and building proximity can change requirements."
      }
    }
  }
};
